"use client";

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, FilterX, Pencil, Plus, Trash2, Webhook } from "lucide-react";

import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { ThemedMultiSelect } from "@/components/ui-system/multi-select";
import { ThemedSelect, tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import { createAction } from "@/lib/api/endpoints/automation-actions";
import { createAlertRule, deleteAlertRule, listAlertRules, updateAlertRule } from "@/lib/api/endpoints/alerts";
import { listDeviceChannels } from "@/lib/api/endpoints/device-channels";
import { listDevicesPage } from "@/lib/api/endpoints/devices";
import { listWorkspaceRoster } from "@/lib/api/endpoints/users";
import type { JsonObject } from "@/lib/api/types/json";
import type { ActionDto, AlertRuleCreateDto, AlertRuleDto, DeviceChannelDto } from "@/lib/api/types";
import type { UserDto } from "@/lib/api/types/enterprise";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type NotificationChannelPrefs = {
  email: boolean;
  message: boolean;
  whatsapp: boolean;
  inApp: boolean;
};

type AlertAudienceKey = "admin" | "distributor" | "users";

type AlertAudiencePrefs = NotificationChannelPrefs & {
  selectAll: boolean;
  userIds: string[];
};

type AlertFormState = {
  name: string;
  deviceId: string;
  channel: string;
  operator: string;
  threshold: string;
  cooldownMinutes: string;
  evaluationWindow: "last_5m" | "last_1h" | "last_24h" | "last_7d";
  aggregation: "avg" | "min" | "max";
  evaluationMode: "latest_value" | "window_avg";
  severity: "info" | "warning" | "critical";
  isActive: boolean;
  audience: {
    admin: AlertAudiencePrefs;
    distributor: AlertAudiencePrefs;
    users: AlertAudiencePrefs;
  };
  emailRecipients: string;
  messageRecipients: string;
  webhookEnabled: boolean;
  webhookUrl: string;
  webhookHeaders: string;
};

const emptyPrefs = (): AlertAudiencePrefs => ({
  email: false,
  message: false,
  whatsapp: false,
  inApp: false,
  selectAll: false,
  userIds: [],
});

const emptyForm = (): AlertFormState => ({
  name: "",
  deviceId: "",
  channel: "",
  operator: ">",
  threshold: "",
  cooldownMinutes: "5",
  evaluationWindow: "last_1h",
  aggregation: "avg",
  evaluationMode: "latest_value",
  severity: "warning",
  isActive: true,
  audience: {
    admin: { ...emptyPrefs(), email: true, message: false, whatsapp: false, inApp: true },
    distributor: emptyPrefs(),
    users: { ...emptyPrefs(), email: false, message: false, whatsapp: false, inApp: true },
  },
  emailRecipients: "",
  messageRecipients: "",
  webhookEnabled: false,
  webhookUrl: "",
  webhookHeaders: "",
});

const triggerClassName = themedSelectTriggerClass;
const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-xl text-caption";

function splitCommaList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function userMatchesAudienceRole(user: UserDto, roleKey: AlertAudienceKey): boolean {
  const role = (user.role || "").trim().toLowerCase();
  if (roleKey === "admin") {
    return role === "admin";
  }
  if (roleKey === "distributor") {
    return role === "distributor";
  }
  return role === "user";
}

function userDisplayName(user: UserDto): string {
  return user.name?.trim() || user.email;
}

function operatorLabel(operator: string): string {
  return (
    {
      ">": "is greater than",
      ">=": "is greater than or equal to",
      "<": "is less than",
      "<=": "is less than or equal to",
      "==": "is equal to",
      "=": "is equal to",
      "!=": "is not equal to",
    }[operator] ?? operator
  );
}

function evaluationWindowLabel(value: AlertFormState["evaluationWindow"]): string {
  return (
    {
      last_5m: "last 5 minutes",
      last_1h: "last 1 hour",
      last_24h: "last 24 hours",
      last_7d: "last 7 days",
    }[value] ?? value
  );
}

function aggregationLabel(value: AlertFormState["aggregation"]): string {
  return (
    {
      avg: "average",
      min: "minimum",
      max: "maximum",
    }[value] ?? value
  );
}

function severityLabel(value: AlertFormState["severity"]): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildRuleIntentSummary(
  form: AlertFormState,
  channelOptions: { value: string; label: string }[],
): string {
  const channelLabel =
    (channelOptions.find((option) => option.value === form.channel)?.label ?? form.channel) ||
    "this channel";
  const thresholdLabel = form.threshold.trim() || "the threshold";
  return `Trigger when ${channelLabel} ${operatorLabel(form.operator)} ${thresholdLabel}.`;
}

function buildEvaluationSummary(form: AlertFormState): string {
  const cooldownRaw = form.cooldownMinutes.trim();
  const cooldownSummary = cooldownRaw && cooldownRaw !== "0"
    ? ` Retriggers are suppressed for ${cooldownRaw} minute${cooldownRaw === "1" ? "" : "s"} after an alert fires.`
    : " Retriggers follow current behavior with no cooldown window.";
  if (form.evaluationMode === "latest_value") {
    return `Checks the latest reading and triggers as soon as it crosses the threshold.${cooldownSummary}`;
  }
  return `Checks the ${aggregationLabel(form.aggregation)} over the ${evaluationWindowLabel(form.evaluationWindow)} before triggering.${cooldownSummary}`;
}

function formatRuleWindow(rule: AlertRuleDto): string {
  const parts = [
    rule.evaluation_mode === "window_avg" ? "Window avg" : "Latest value",
    rule.aggregation ? rule.aggregation.toUpperCase() : null,
    rule.evaluation_window ? rule.evaluation_window.replaceAll("_", " ") : null,
  ].filter(Boolean);
  return parts.join(" - ");
}

function formatActionSummary(actions: ActionDto[] | undefined): string {
  if (!actions?.length) return "No delivery actions";
  const labels = actions.map((action) =>
    action.type === "webhook"
      ? "Webhook"
      : action.type === "email"
        ? "Email"
        : action.type === "whatsapp"
          ? "Whatsapp"
          : action.type,
  );
  return labels.join(", ");
}

function parseWebhookHeaders(raw: string): Record<string, string> | null {
  if (!raw.trim()) return {};
  const parsed = JSON.parse(raw) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Webhook headers must be a JSON object.");
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    out[key] = String(value);
  }
  return out;
}

function hasNotificationConfig(form: AlertFormState): boolean {
  const audienceEnabled = Object.values(form.audience).some((prefs) => prefs.email || prefs.message || prefs.whatsapp || prefs.inApp);
  return audienceEnabled || !!form.emailRecipients.trim() || !!form.messageRecipients.trim();
}

function buildEmailActionConfig(form: AlertFormState): JsonObject {
  return {
    audience: form.audience,
    email_recipients: splitCommaList(form.emailRecipients),
    message_recipients: splitCommaList(form.messageRecipients),
    subject: form.name.trim() ? `Alert: ${form.name.trim()}` : "Alert triggered",
  };
}

function ruleToForm(rule: AlertRuleDto): AlertFormState {
  const emailAction = (rule.actions ?? []).find((action) => action.type === "email");
  const webhookAction = (rule.actions ?? []).find((action) => action.type === "webhook");
  const audience = (emailAction?.config?.audience as AlertFormState["audience"] | undefined) ?? emptyForm().audience;
  const emailRecipients = Array.isArray(emailAction?.config?.email_recipients)
    ? (emailAction?.config?.email_recipients as string[]).join(", ")
    : "";
  const messageRecipients = Array.isArray(emailAction?.config?.message_recipients)
    ? (emailAction?.config?.message_recipients as string[]).join(", ")
    : "";
  const headers = webhookAction?.config?.headers ? JSON.stringify(webhookAction.config.headers, null, 2) : "";
  return {
    name: rule.name ?? "",
    deviceId: String(rule.data_source?.device_id ?? ""),
    channel: String(rule.data_source?.channel ?? ""),
    operator: rule.operator ?? ">",
    threshold: rule.threshold != null ? String(rule.threshold) : "",
    cooldownMinutes: rule.cooldown_minutes != null ? String(rule.cooldown_minutes) : "",
    evaluationWindow: (rule.evaluation_window as AlertFormState["evaluationWindow"]) ?? "last_1h",
    aggregation: (rule.aggregation as AlertFormState["aggregation"]) ?? "avg",
    evaluationMode: (rule.evaluation_mode as AlertFormState["evaluationMode"]) ?? "latest_value",
    severity: (rule.severity as AlertFormState["severity"]) ?? "warning",
    isActive: rule.is_active,
    audience: {
      admin: { ...emptyPrefs(), ...(audience.admin ?? {}) },
      distributor: { ...emptyPrefs(), ...(audience.distributor ?? {}) },
      users: { ...emptyPrefs(), ...(audience.users ?? {}) },
    },
    emailRecipients,
    messageRecipients,
    webhookEnabled: !!webhookAction,
    webhookUrl: String(webhookAction?.config?.url ?? ""),
    webhookHeaders: headers,
  };
}

function formatAlertThresholdLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return "true";
  if (normalized === "false") return "false";
  return value.trim() || "the threshold";
}

function getAlertOperatorOptions(
  channelType: DeviceChannelDto["data_type"] | null,
): { value: string; label: string }[] {
  if (channelType === "boolean") {
    return [{ value: "=", label: "=" }];
  }
  if (channelType === "string") {
    return [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
    ];
  }
  return [
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "==", label: "==" },
    { value: "!=", label: "!=" },
  ];
}

function normalizeBooleanThreshold(raw: string): boolean {
  const normalized = raw.trim().toLowerCase();
  if (["true", "1", "open", "on", "yes"].includes(normalized)) return true;
  if (["false", "0", "close", "closed", "off", "no"].includes(normalized)) return false;
  throw new Error("Boolean threshold must be true or false.");
}

function parseAlertThreshold(
  raw: string,
  channelType: DeviceChannelDto["data_type"] | null,
): number | string | boolean {
  const trimmed = raw.trim();
  if (!trimmed) throw new Error("Threshold is required.");
  if (channelType === "boolean") {
    return normalizeBooleanThreshold(trimmed);
  }
  if (channelType === "string") {
    return trimmed;
  }
  const threshold = Number(trimmed);
  if (!Number.isFinite(threshold)) throw new Error("Threshold must be a valid number.");
  return threshold;
}

async function buildActionIds(form: AlertFormState, tenantId: string): Promise<string[]> {
  const actionIds: string[] = [];

  if (hasNotificationConfig(form)) {
    const emailAction = await createAction({
      tenant_id: tenantId,
      type: "email",
      config: buildEmailActionConfig(form),
    });
    actionIds.push(emailAction.id);
  }

  if (form.webhookEnabled && form.webhookUrl.trim()) {
    const webhookAction = await createAction({
      tenant_id: tenantId,
      type: "webhook",
      config: {
        url: form.webhookUrl.trim(),
        headers: parseWebhookHeaders(form.webhookHeaders) ?? {},
      },
    });
    actionIds.push(webhookAction.id);
  }

  return actionIds;
}

function AlertRuleModal({
  title,
  form,
  setForm,
  channelOptions,
  channels,
  devices,
  workspaceUsers,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  title: string;
  form: AlertFormState;
  setForm: React.Dispatch<React.SetStateAction<AlertFormState>>;
  channelOptions: { value: string; label: string }[];
  channels: DeviceChannelDto[];
  devices: { value: string; label: string; description?: string }[];
  workspaceUsers: UserDto[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const [showAdvancedEvaluation, setShowAdvancedEvaluation] = useState(false);
  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.key === form.channel) ?? null,
    [channels, form.channel],
  );
  const selectedChannelType = selectedChannel?.data_type ?? null;
  const operatorOptions = useMemo(() => getAlertOperatorOptions(selectedChannelType), [selectedChannelType]);
  const ruleIntentSummary = useMemo(
    () => buildRuleIntentSummary({ ...form, threshold: formatAlertThresholdLabel(form.threshold) }, channelOptions),
    [channelOptions, form],
  );
  const evaluationSummary = buildEvaluationSummary(form);

  useEffect(() => {
    setShowAdvancedEvaluation(form.evaluationMode !== "latest_value");
  }, [form.evaluationMode, title]);

  useEffect(() => {
    if (selectedChannelType !== "number") {
      setShowAdvancedEvaluation(false);
      setForm((prev) =>
        prev.evaluationMode === "latest_value" && prev.aggregation === "avg"
          ? prev
          : { ...prev, evaluationMode: "latest_value", aggregation: "avg" },
      );
    }
  }, [selectedChannelType, setForm]);

  useEffect(() => {
    if (selectedChannelType === "boolean") {
      const normalized = form.threshold.trim().toLowerCase();
      if (normalized !== "true" && normalized !== "false") {
        setForm((prev) => ({ ...prev, threshold: "true" }));
      }
    }
  }, [form.threshold, selectedChannelType, setForm]);

  useEffect(() => {
    if (operatorOptions.length && !operatorOptions.some((option) => option.value === form.operator)) {
      setForm((prev) => ({ ...prev, operator: operatorOptions[0]!.value }));
    }
  }, [form.operator, operatorOptions, setForm]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(15, 23, 42, 0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 24,
          border: "1px solid var(--card-border)",
          background: "var(--bg-surface)",
          boxShadow: "0 24px 60px -32px rgba(15,23,42,0.24)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--card-border)", background: "var(--bg-surface)" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937" }}>{title}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>
            Backed by the real alert rule engine and scheduler.
          </div>
        </div>

        <div style={{ padding: 20, display: "grid", gap: 16, maxHeight: "75vh", overflowY: "auto" }}>
          {error ? (
            <div style={{ border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)", borderRadius: 12, padding: "10px 12px", fontSize: 12 }}>
              {error}
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Rule name
              <input className="ui-control" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Device
              <ThemedSelect
                value={form.deviceId}
                onChange={(value) =>
                  setForm((p) => ({
                    ...p,
                    deviceId: value,
                    channel: "",
                    operator: ">",
                    threshold: "",
                    evaluationMode: "latest_value",
                    aggregation: "avg",
                  }))
                }
                options={[{ value: "", label: "Select device..." }, ...devices]}
                ariaLabel="Alert rule device"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Channel
              <ThemedSelect
                value={form.channel}
                onChange={(value) =>
                  setForm((p) => ({
                    ...p,
                    channel: value,
                    operator: ">",
                    threshold: "",
                    evaluationMode: "latest_value",
                    aggregation: "avg",
                  }))
                }
                options={[{ value: "", label: "Select channel..." }, ...channelOptions]}
                ariaLabel="Alert rule channel"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Operator
              <ThemedSelect
                value={form.operator}
                onChange={(value) => setForm((p) => ({ ...p, operator: value }))}
                options={operatorOptions}
                ariaLabel="Alert operator"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Threshold
              {selectedChannelType === "boolean" ? (
                <ThemedSelect
                  value={form.threshold.trim().toLowerCase() === "false" ? "false" : "true"}
                  onChange={(value) => setForm((p) => ({ ...p, threshold: value }))}
                  options={[
                    { value: "true", label: "True" },
                    { value: "false", label: "False" },
                  ]}
                  ariaLabel="Alert threshold"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              ) : (
                <input
                  className="ui-control"
                  value={form.threshold}
                  onChange={(e) => setForm((p) => ({ ...p, threshold: e.target.value }))}
                  inputMode={selectedChannelType === "number" ? "decimal" : "text"}
                  placeholder={selectedChannelType === "string" ? "Enter matching text" : undefined}
                />
              )}
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Cooldown (minutes)
              <input
                className="ui-control"
                value={form.cooldownMinutes}
                onChange={(e) => setForm((p) => ({ ...p, cooldownMinutes: e.target.value }))}
                placeholder="5"
                inputMode="numeric"
              />
            </label>
          </div>

          <div style={{ border: "1px solid var(--card-border)", borderRadius: 18, background: "var(--card)", padding: 16, display: "grid", gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>
              Alert summary
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>{ruleIntentSummary}</div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              Severity: {severityLabel(form.severity)}. {evaluationSummary}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Severity
              <ThemedSelect
                value={form.severity}
                onChange={(value) => setForm((p) => ({ ...p, severity: value as AlertFormState["severity"] }))}
                options={[
                  { value: "info", label: "Info" },
                  { value: "warning", label: "Warning" },
                  { value: "critical", label: "Critical" },
                ]}
                ariaLabel="Alert severity"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              Status
              <ThemedSelect
                value={form.isActive ? "active" : "paused"}
                onChange={(value) => setForm((p) => ({ ...p, isActive: value === "active" }))}
                options={[
                  { value: "active", label: "Active" },
                  { value: "paused", label: "Paused" },
                ]}
                ariaLabel="Alert status"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
          </div>

          <div style={{ border: "1px solid var(--card-border)", borderRadius: 18, background: "#fff", padding: 16, display: "grid", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>
                  Advanced evaluation
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "var(--muted)" }}>
                  Fine-tune how the rule checks telemetry before it raises an alert.
                </div>
              </div>
              <button
                type="button"
                disabled={selectedChannelType !== "number"}
                onClick={() => setShowAdvancedEvaluation((current) => !current)}
                style={{
                  minHeight: 36,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: "1px solid var(--card-border)",
                  background: "var(--card)",
                  cursor: selectedChannelType === "number" ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  color: selectedChannelType === "number" ? "#1f2937" : "var(--muted)",
                  opacity: selectedChannelType === "number" ? 1 : 0.7,
                }}
              >
                {showAdvancedEvaluation ? "Hide advanced" : "Show advanced"}
              </button>
            </div>

            {selectedChannelType !== "number" ? (
              <div style={{ borderRadius: 14, background: "rgba(248, 250, 252, 0.8)", padding: "12px 14px", fontSize: 13, color: "var(--muted)" }}>
                {selectedChannelType === "boolean"
                  ? "Boolean channels alert on the latest true or false state only."
                  : selectedChannelType === "string"
                    ? "String channels alert on the latest text value only."
                    : "Select a channel to configure evaluation behavior."}
              </div>
            ) : showAdvancedEvaluation ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: form.evaluationMode === "window_avg" ? "1.2fr 1fr 1fr" : "1fr", gap: 14, alignItems: "start" }}>
                <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                  Trigger based on
                  <ThemedSelect
                    value={form.evaluationMode}
                    onChange={(value) =>
                      setForm((p) => ({
                        ...p,
                        evaluationMode: value as AlertFormState["evaluationMode"],
                        aggregation: value === "latest_value" ? "avg" : p.aggregation,
                      }))
                    }
                    options={[
                      { value: "latest_value", label: "Latest reading" },
                      { value: "window_avg", label: "Average over time" },
                    ]}
                    ariaLabel="Alert evaluation mode"
                    triggerClassName={triggerClassName}
                    menuClassName={menuClassName}
                    optionClassName={optionClassName}
                  />
                </label>

                {form.evaluationMode === "window_avg" ? (
                  <>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                      Over what time?
                      <ThemedSelect
                        value={form.evaluationWindow}
                        onChange={(value) => setForm((p) => ({ ...p, evaluationWindow: value as AlertFormState["evaluationWindow"] }))}
                        options={[
                          { value: "last_5m", label: "Last 5 minutes" },
                          { value: "last_1h", label: "Last 1 hour" },
                          { value: "last_24h", label: "Last 24 hours" },
                          { value: "last_7d", label: "Last 7 days" },
                        ]}
                        ariaLabel="Alert evaluation window"
                        triggerClassName={triggerClassName}
                        menuClassName={menuClassName}
                        optionClassName={optionClassName}
                      />
                    </label>
                    <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                      Combine readings using
                      <ThemedSelect
                        value={form.aggregation}
                        onChange={(value) => setForm((p) => ({ ...p, aggregation: value as AlertFormState["aggregation"] }))}
                        options={[
                          { value: "avg", label: "Average" },
                          { value: "min", label: "Minimum" },
                          { value: "max", label: "Maximum" },
                        ]}
                        ariaLabel="Alert aggregation"
                        triggerClassName={triggerClassName}
                        menuClassName={menuClassName}
                        optionClassName={optionClassName}
                      />
                    </label>
                  </>
                ) : null}
              </div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {form.evaluationMode === "latest_value"
                    ? "Best for instant threshold breaches."
                    : "Best when you want to ignore brief spikes and judge the trend."}
                </div>
              </div>
            ) : (
              <div style={{ borderRadius: 14, background: "rgba(248, 250, 252, 0.8)", padding: "12px 14px", fontSize: 13, color: "var(--muted)" }}>
                {evaluationSummary}
              </div>
            )}
          </div>

          <div style={{ border: "1px solid var(--card-border)", borderRadius: 18, background: "#fff", padding: 16, display: "grid", gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>
                Notifications
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: "var(--muted)" }}>
                Email and in-app notifications are live. Message and Whatsapp delivery remain future channels.
              </div>
            </div>

            {([
              ["admin", "Admins"],
              ["distributor", "Distributors"],
              ["users", "Users"],
            ] as const).map(([key, label]) => {
              const prefs = form.audience[key];
              const roleUsers = workspaceUsers
                .filter((user) => userMatchesAudienceRole(user, key))
                .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
              return (
                <div key={key} style={{ border: "1px solid var(--card-border)", borderRadius: 14, background: "var(--bg-surface)", padding: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", marginBottom: 10 }}>{label}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
                    {([
                      ["inApp", "In app"],
                      ["email", "Email"],
                      ["message", "Message"],
                      ["whatsapp", "Whatsapp"],
                    ] as const).map(([channelKey, channelLabel]) => (
                      <label key={channelKey} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--card-border)", borderRadius: 12, background: "#fff", padding: "10px 12px", fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={prefs[channelKey]}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              audience: {
                                ...prev.audience,
                                [key]: {
                                  ...prev.audience[key],
                                  [channelKey]: e.target.checked,
                                },
                              },
                            }))
                          }
                        />
                        <span>{channelLabel}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop: 12, border: "1px solid var(--card-border)", borderRadius: 12, background: "#fff", padding: 12, display: "grid", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#1f2937" }}>
                      <input
                        type="checkbox"
                        checked={prefs.selectAll}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              [key]: {
                                ...prev.audience[key],
                                selectAll: e.target.checked,
                                userIds: e.target.checked ? [] : prev.audience[key].userIds,
                              },
                            },
                          }))
                        }
                      />
                      <span>Select all {label.toLowerCase()}</span>
                    </label>
                    <div style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                      <span>Selected users</span>
                      <ThemedMultiSelect
                        value={prefs.userIds}
                        onChange={(nextUserIds) =>
                          setForm((prev) => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              [key]: {
                                ...prev.audience[key],
                                userIds: nextUserIds,
                              },
                            },
                          }))
                        }
                        options={roleUsers.map((user) => ({
                          value: user.id,
                          label: userDisplayName(user),
                          description: user.email,
                        }))}
                        placeholder={`Choose ${label.toLowerCase()}...`}
                        searchPlaceholder={`Search ${label.toLowerCase()}...`}
                        disabled={prefs.selectAll || roleUsers.length === 0}
                        ariaLabel={`${label} users`}
                      />
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      {prefs.selectAll
                        ? `All ${label.toLowerCase()} in this workspace will be notified.`
                        : roleUsers.length > 0
                          ? `${prefs.userIds.length} selected.`
                          : `No ${label.toLowerCase()} available in this workspace.`}
                    </div>
                  </div>
                </div>
              );
            })}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                Additional email recipients
                <input className="ui-control" value={form.emailRecipients} onChange={(e) => setForm((p) => ({ ...p, emailRecipients: e.target.value }))} placeholder="ops@example.com, admin@example.com" />
              </label>
              <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                Message recipients
                <input className="ui-control" value={form.messageRecipients} onChange={(e) => setForm((p) => ({ ...p, messageRecipients: e.target.value }))} placeholder="+919876543210, +911234567890" />
              </label>
            </div>
          </div>

          <div style={{ border: "1px solid var(--card-border)", borderRadius: 18, background: "#fff", padding: 16, display: "grid", gap: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
              <input type="checkbox" checked={form.webhookEnabled} onChange={(e) => setForm((p) => ({ ...p, webhookEnabled: e.target.checked }))} />
              Send webhook
            </label>
            {form.webhookEnabled ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                  Webhook URL
                  <input className="ui-control" value={form.webhookUrl} onChange={(e) => setForm((p) => ({ ...p, webhookUrl: e.target.value }))} placeholder="https://example.com/hooks/alerts" />
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
                  Headers (JSON)
                  <textarea className="ui-control p-3 text-xs" rows={4} value={form.webhookHeaders} onChange={(e) => setForm((p) => ({ ...p, webhookHeaders: e.target.value }))} placeholder={'{\n  "Authorization": "Bearer ..."\n}'} />
                </label>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "0 22px 22px" }}>
          <button type="button" onClick={onClose} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--card-border)", background: "var(--card)", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={onSubmit} disabled={busy} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--btn-primary-to)", background: "var(--btn-primary-to)", color: "var(--btn-primary-fg)", cursor: "pointer" }}>
            {busy ? "Saving..." : "Save alert"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AutomationsPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canCreateAlerts = hasModulePermission(user, permissionMap, "automations", "create");
  const canEditAlerts = hasModulePermission(user, permissionMap, "automations", "edit");
  const canDeleteAlerts = hasModulePermission(user, permissionMap, "automations", "delete");
  const isSuperAdmin = user?.role === "super_admin";
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<AlertRuleDto | null>(null);
  const [form, setForm] = useState<AlertFormState>(() => emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const nextToast = (location.state as { toast?: string } | null)?.toast;
    if (!nextToast) return;
    setToast(nextToast);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const effectiveWorkspaceFilter = isSuperAdmin ? (tenantId ?? "all") : (tenantId ?? "");
  const isAllWorkspacesScope = isSuperAdmin && !tenantId;
  const formTenantId = dialog === "edit" && editing?.tenant_id
    ? editing.tenant_id
    : !isAllWorkspacesScope
      ? effectiveWorkspaceFilter
      : "";


  const rulesQuery = useQuery({
    queryKey: ["workspace-alert-rules", effectiveWorkspaceFilter || "none"],
    queryFn: () =>
      listAlertRules(
        isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null),
        isAllWorkspacesScope ? "all" : null,
      ),
    enabled: isAllWorkspacesScope || Boolean(effectiveWorkspaceFilter),
  });

  const devicesQuery = useQuery({
    queryKey: ["workspace-alert-rule-devices", formTenantId || ""],
    queryFn: () => listDevicesPage({ tenantId: formTenantId, limit: 200, offset: 0 }),
    enabled: Boolean(formTenantId) && (dialog !== null || !isAllWorkspacesScope),
    staleTime: 30_000,
  });
  const usersQuery = useQuery({
    queryKey: ["workspace-alert-rule-users", formTenantId || ""],
    queryFn: async () => (await listWorkspaceRoster(formTenantId, { moduleKey: "automations" })).items,
    enabled: Boolean(formTenantId) && (dialog !== null || canCreateAlerts || canEditAlerts),
    staleTime: 30_000,
  });

  const channelsQuery = useQuery({
    queryKey: ["workspace-alert-rule-channels", form.deviceId],
    queryFn: () => listDeviceChannels(form.deviceId),
    enabled: !!form.deviceId && dialog !== null,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!formTenantId || isAllWorkspacesScope) throw new Error("Select a workspace before creating an alert rule.");
      if (!form.name.trim()) throw new Error("Rule name is required.");
      if (!form.deviceId) throw new Error("Device selection is required.");
      if (!form.channel.trim()) throw new Error("Channel selection is required.");
      const selectedChannel = (channelsQuery.data ?? []).find((channel) => channel.key === form.channel) ?? null;
      const selectedChannelType = selectedChannel?.data_type ?? null;
      const threshold = parseAlertThreshold(form.threshold, selectedChannelType);
      const cooldownMinutesRaw = form.cooldownMinutes.trim();
      const cooldownMinutes = cooldownMinutesRaw === "" ? null : Number(cooldownMinutesRaw);
      if (cooldownMinutes !== null && (!Number.isInteger(cooldownMinutes) || cooldownMinutes < 0)) {
        throw new Error("Cooldown window must be a whole number of minutes.");
      }

      const actionIds = await buildActionIds(form, formTenantId);
      const body: AlertRuleCreateDto = {
        name: form.name.trim(),
        tenant_id: formTenantId,
        data_source: {
          device_id: form.deviceId,
          channel: form.channel.trim(),
        },
        operator: form.operator,
        threshold,
        cooldown_minutes: cooldownMinutes,
        evaluation_window: selectedChannelType === "number" ? form.evaluationWindow : null,
        aggregation: selectedChannelType === "number" ? form.aggregation : undefined,
        evaluation_mode: selectedChannelType === "number" ? form.evaluationMode : "latest_value",
        severity: form.severity,
        action_ids: actionIds,
        is_active: form.isActive,
      };
      return createAlertRule(body);
    },
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ["workspace-alert-rules"] });
      setDialog(null);
      setEditing(null);
      setForm(emptyForm());
      setFormError(null);
      setToast(`Alert "${created.name ?? "rule"}" created.`);
    },
    onError: (error) => {
      setFormError(error instanceof Error ? error.message : "Could not create alert rule.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const targetTenantId = editing?.tenant_id ?? formTenantId;
      if (!targetTenantId || !editing) throw new Error("Editing context is missing.");
      if (!form.name.trim()) throw new Error("Rule name is required.");
      if (!form.deviceId) throw new Error("Device selection is required.");
      if (!form.channel.trim()) throw new Error("Channel selection is required.");
      const selectedChannel = (channelsQuery.data ?? []).find((channel) => channel.key === form.channel) ?? null;
      const selectedChannelType = selectedChannel?.data_type ?? null;
      const threshold = parseAlertThreshold(form.threshold, selectedChannelType);
      const cooldownMinutesRaw = form.cooldownMinutes.trim();
      const cooldownMinutes = cooldownMinutesRaw === "" ? null : Number(cooldownMinutesRaw);
      if (cooldownMinutes !== null && (!Number.isInteger(cooldownMinutes) || cooldownMinutes < 0)) {
        throw new Error("Cooldown window must be a whole number of minutes.");
      }

      const actionIds = await buildActionIds(form, targetTenantId);
      return updateAlertRule(editing.id, {
        name: form.name.trim(),
        tenant_id: targetTenantId,
        data_source: {
          device_id: form.deviceId,
          channel: form.channel.trim(),
        },
        operator: form.operator,
        threshold,
        cooldown_minutes: cooldownMinutes,
        evaluation_window: selectedChannelType === "number" ? form.evaluationWindow : null,
        aggregation: selectedChannelType === "number" ? form.aggregation : undefined,
        evaluation_mode: selectedChannelType === "number" ? form.evaluationMode : "latest_value",
        severity: form.severity,
        action_ids: actionIds,
        is_active: form.isActive,
      });
    },
    onSuccess: async (updated) => {
      await qc.invalidateQueries({ queryKey: ["workspace-alert-rules"] });
      setDialog(null);
      setEditing(null);
      setForm(emptyForm());
      setFormError(null);
      setToast(`Alert "${updated.name ?? "rule"}" updated.`);
    },
    onError: (error) => {
      setFormError(error instanceof Error ? error.message : "Could not update alert rule.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => deleteAlertRule(ruleId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["workspace-alert-rules"] });
      setToast("Alert deleted.");
    },
  });

  const rules = rulesQuery.data ?? [];

  const filtered = useMemo(() => {
    return rules.filter((rule) => {
      const searchText = [
        rule.name ?? "",
        String(rule.data_source?.channel ?? ""),
        String(rule.data_source?.device_id ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !search.trim() || searchText.includes(search.trim().toLowerCase());
      const matchesStatus =
        !statusFilter || (statusFilter === "active" ? rule.is_active : !rule.is_active);
      const actionTypes = (rule.actions ?? []).map((action) => action.type);
      const matchesAction = !actionFilter || actionTypes.includes(actionFilter);
      return matchesSearch && matchesStatus && matchesAction;
    });
  }, [rules, search, statusFilter, actionFilter]);

  const deviceNameMap = useMemo(() => {
    return new Map((devicesQuery.data?.items ?? []).map((device) => [device.id, device.name?.trim() || device.device_uid]));
  }, [devicesQuery.data?.items]);

  const devices = (devicesQuery.data?.items ?? []).map((device) => ({
    value: device.id,
    label: device.name?.trim() || device.device_uid,
    description: device.device_uid,
  }));
  const workspaceUsers = usersQuery.data ?? [];

  const channelOptions = (channelsQuery.data ?? []).map((channel) => ({
    value: channel.key,
    label: channel.label?.trim() || channel.key,
  }));

  const hasFilters = !!search.trim() || !!statusFilter || !!actionFilter;

  return (
    <EnterprisePageShell
      title="Alerts"
    >
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}

      <div style={{ display: "grid", gap: 24 }}>
        <ListSurface>
          <ListSurfaceHeader
            title="Alert rules"
            meta={
              <ListCount
                total={rules.length}
                filtered={filtered.length}
                noun={filtered.length === 1 ? "alert rule" : "alert rules"}
              />
            }
            controls={
              <ListToolbar>
                <ListToolbarGroup grow>
                  <input
                    className={tableHeaderSearchInputClass}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search alerts"
                  />
                </ListToolbarGroup>
                <ListToolbarGroup>
                  <ThemedSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: "", label: "All statuses" },
                      { value: "active", label: "Active" },
                      { value: "paused", label: "Paused" },
                    ]}
                    ariaLabel="Alert status filter"
                    className="min-w-[8rem]"
                    triggerClassName={tableHeaderSelectTriggerClass}
                    menuClassName={menuClassName}
                    optionClassName={optionClassName}
                  />
                </ListToolbarGroup>
                <ListToolbarGroup>
                  <ThemedSelect
                    value={actionFilter}
                    onChange={setActionFilter}
                    options={[
                      { value: "", label: "All delivery types" },
                      { value: "email", label: "Email" },
                      { value: "webhook", label: "Webhook" },
                    ]}
                    ariaLabel="Alert action filter"
                    className="min-w-[9rem]"
                    triggerClassName={tableHeaderSelectTriggerClass}
                    menuClassName={menuClassName}
                    optionClassName={optionClassName}
                  />
                </ListToolbarGroup>
                {hasFilters ? (
                  <ToolbarIconButton
                    icon={FilterX}
                    label="Clear filters"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("");
                      setActionFilter("");
                    }}
                  />
                ) : null}
                {canCreateAlerts ? (
                  <ToolbarIconButton
                    icon={Plus}
                    label="New alert"
                    variant="primary"
                    disabled={isAllWorkspacesScope}
                    onClick={() => {
                      setEditing(null);
                      setForm(emptyForm());
                      setFormError(null);
                      setDialog("create");
                    }}
                  />
                ) : null}
              </ListToolbar>
            }
          />
          <ListSurfaceBody>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.3fr 1.5fr 1.2fr 1fr 112px",
              gap: 16,
              padding: "14px 18px",
              borderBottom: "1px solid var(--table-border)",
              background: "var(--table-header-bg)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--muted)",
            }}
          >
            <div>Rule</div>
            <div>Source</div>
            <div>Trigger</div>
            <div>Delivery</div>
            <div>Status</div>
            <div style={{ textAlign: "right" }}>Actions</div>
          </div>

          {rulesQuery.isLoading ? (
            <div className="p-ds-4" aria-label="Loading automation rules">
              {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "36px 18px", textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
              No alert rules match this filter.
            </div>
          ) : (
            filtered.map((rule, index) => {
              const actionSummary = formatActionSummary(rule.actions);
              const stripedBackground = index % 2 === 1 ? "color-mix(in srgb, var(--muted-bg) 38%, transparent)" : "transparent";
              return (
                <div
                  key={rule.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.3fr 1.5fr 1.2fr 1fr 112px",
                    gap: 16,
                    padding: "16px 18px",
                    borderBottom: "1px solid var(--table-border)",
                    background: stripedBackground,
                    alignItems: "center",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--foreground)" }}>{rule.name ?? "Untitled alert"}</div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>{formatRuleWindow(rule)}</div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
                      {deviceNameMap.get(String(rule.data_source?.device_id ?? "")) ?? String(rule.data_source?.device_id ?? "—")}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>{String(rule.data_source?.channel ?? "—")}</div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
                      {rule.operator ?? "—"} {rule.threshold ?? "—"}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
                      {(rule.severity ?? "warning").replace("_", " ")}
                      {rule.cooldown_minutes != null && rule.cooldown_minutes > 0 ? ` · Cooldown ${rule.cooldown_minutes}m` : ""}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {(rule.actions ?? []).some((action) => action.type === "email") ? <Bell size={14} color="#c2410c" /> : null}
                      {(rule.actions ?? []).some((action) => action.type === "webhook") ? <Webhook size={14} color="#c2410c" /> : null}
                      <span style={{ fontSize: 12, color: "var(--muted)" }}>{actionSummary}</span>
                    </div>
                  </div>
                  <div>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: 26,
                        padding: "0 10px",
                        borderRadius: 999,
                        border: "1px solid " + (rule.is_active ? "#86efac" : "#fecaca"),
                        background: rule.is_active ? "#ecfdf3" : "#fff1f2",
                        color: rule.is_active ? "#166534" : "#b42318",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {rule.is_active ? "Active" : "Paused"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    {canEditAlerts ? (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(rule);
                          setForm(ruleToForm(rule));
                          setFormError(null);
                          setDialog("edit");
                        }}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          border: "1px solid rgba(59,130,246,0.18)",
                          background: "rgba(59,130,246,0.10)",
                          color: "#1d4ed8",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        aria-label={`Edit ${rule.name ?? "alert"}`}
                      >
                        <Pencil size={14} />
                      </button>
                    ) : null}
                    {canDeleteAlerts ? (
                      <button
                        type="button"
                        onClick={() => {
                          void deleteMutation.mutate(rule.id);
                        }}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 10,
                          border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)",
                          background: "#fff5f5",
                          color: "#b42318",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        aria-label={`Delete ${rule.name ?? "alert"}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
          </ListSurfaceBody>
        </ListSurface>
      </div>

      {dialog ? (
        <AlertRuleModal
          title={dialog === "create" ? "New alert rule" : `Edit ${editing?.name ?? "alert rule"}`}
          form={form}
          setForm={setForm}
          devices={devices}
          channelOptions={channelOptions}
          channels={channelsQuery.data ?? []}
          workspaceUsers={workspaceUsers}
          busy={createMutation.isPending || updateMutation.isPending}
          error={formError}
          onClose={() => {
            setDialog(null);
            setEditing(null);
            setFormError(null);
          }}
          onSubmit={() => {
            if (dialog === "create") {
              void createMutation.mutate();
            } else {
              void updateMutation.mutate();
            }
          }}
        />
      ) : null}
    </EnterprisePageShell>
  );
}
